'use strict';

import { updateUIFromShadowRoot } from '@core/i18n/window-language';
import { addDownloadOptionsList } from '@core/utils/download';
import { updateSessionStoreValue } from '@core/utils/sessionStore';
import { goToDetailsView, goToReportDetails } from '@core/utils/navigate';
import { WESOG_SESSION_STORE_PARAMS } from '@core/constants/STORE';
import {
    UPDATE_LANGUAGE_EVENT,
    TRIGGER_SHOW_DOWNLOAD_OPTIONS_ZOOM_MODAL_EVENT,
} from '@core/constants/EVENTS';
import { TRIGGER_CLOSE_COMMON_CUSTOM_MODAL_EVENT } from '@components/templates/CommonCustomModalComponent/CONSTANTS';
import { ACTIVE_ID_ATTRIBUTE } from '@core/constants/ATTRIBUTES';
import {
    globalSharedStyles,
    imageIconsStyles,
    typographySharedStyles,
    customCommonStyles,
    commonCustomModalStyles,
    customMenuDownStyleSheet,
    customDownloadOptionsStyles,
} from '@core/styles';

import {
    ZOOM_MODAL_WEB_COMPONENT,
    CUSTOM_COMMON_MODAL_WEB_COMPONENT_ATTRIBUTE,
    CUSTOM_ZOOM_MODAL_LOADING_SPINNER_WEB_COMPONENT,
} from './CONSTANTS';
import htmlContent from './ZoomModalComponent.html';
import cssContent from './ZoomModalComponent.css';
import {
    hideUnnecessaryIconsForLightbox,
    overrideCommonCustomModalContainerStyle,
    handleActivesImages,
    assignActiveIdToElements,
    reproduceVideo,
    stopVideo,
    renderImageContent,
    renderNavigationArrows,
    renderActionButtons,
    setupDeleteIcon,
    clearDownloadOptions,
} from './utils';

/**
 * ZoomModalComponent
 *
 * A web component that displays a modal with zoom functionality for images and videos.
 * Supports navigation between items, video playback, and various action buttons.
 *
 * @class ZoomModalComponent
 * @extends {HTMLElement}
 */
class ZoomModalComponent extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });

        /** @type {HTMLTemplateElement} */
        const template = document.createElement('template');
        template.innerHTML = htmlContent;

        const styleSheet = new CSSStyleSheet();
        styleSheet.replaceSync(cssContent);

        this.shadowRoot.adoptedStyleSheets = [
            globalSharedStyles,
            imageIconsStyles,
            typographySharedStyles,
            commonCustomModalStyles,
            customCommonStyles,
            customMenuDownStyleSheet,
            customDownloadOptionsStyles,
            styleSheet,
        ];
        this.shadowRoot.appendChild(template.content.cloneNode(true));

        // Initialize properties
        this._active = null;
        this._imageId = null;
        this._preActiveId = null;
        this._posActiveId = null;
        this._fetchVideo = fetch;
        this._videoElement = null;
        this._observer = null;
        this._timeoutId = null;

        // Bind methods to preserve context
        this._handleTranslationHelperBound = this._handleTranslationHelper.bind(this);
        this._handleAttributeChangeBound = this._handleAttributeChange.bind(this);
        this._handleDownloadOptionsBound = this._handleDownloadOptions.bind(this);
        this._handleCloseModalBound = this._handleCloseModal.bind(this);
        this._handleNavigationBound = this._handleNavigation.bind(this);

        // Navigation handlers - bound once and reused
        this._handlePrevClickBound = this._handlePrevClick.bind(this);
        this._handleNextClickBound = this._handleNextClick.bind(this);
        this._handleSeeMoreClickBound = this._handleSeeMoreClick.bind(this);
        this._handleSeeReportClickBound = this._handleSeeReportClick.bind(this);
    }

    /**
     * Called when the element is connected to the DOM.
     * Sets up event listeners and initializes the component.
     * @returns {void}
     */
    connectedCallback() {
        this._addEventListeners();
        this._preRender();
    }

    /**
     * Called when the element is disconnected from the DOM.
     * Cleans up event listeners and resources to prevent memory leaks.
     * @returns {void}
     */
    disconnectedCallback() {
        this._removeEventListeners();
        this._cleanup();
    }

    /**
     * Show the modal and handle translations.
     * @returns {void}
     */
    showModal() {
        this._handleTranslationHelper();
        this.shadowRoot
            .querySelector(CUSTOM_COMMON_MODAL_WEB_COMPONENT_ATTRIBUTE)
            .showModal();
    }

    /**
     * Render the component with current data.
     * @returns {void}
     */
    render() {
        if (!this._active) return;

        clearDownloadOptions(this.shadowRoot);
        setupDeleteIcon(this.shadowRoot);
        renderImageContent(
            this.shadowRoot,
            this._active,
            this._videoElement,
            this._reproduceVideo.bind(this),
        );
        renderNavigationArrows(this.shadowRoot, this._preActiveId, this._posActiveId);
        renderActionButtons(this.shadowRoot, this._active);
        // Event listeners are already set up in _preRender, no need to re-add them
    }

    /**
     * Pre-render setup for the component.
     * @returns {void}
     * @private
     */
    _preRender() {
        // Hide the loading spinner
        this.shadowRoot.querySelector(
            CUSTOM_ZOOM_MODAL_LOADING_SPINNER_WEB_COMPONENT,
        ).style.display = 'none';

        overrideCommonCustomModalContainerStyle(this.shadowRoot);
        this._setupButtonHandlers(true);
        this._setupNavigationHandlers(true);
        hideUnnecessaryIconsForLightbox(this.shadowRoot);
    }

    /**
     * Set up navigation handlers for next/previous buttons.
     * @returns {void}
     * @private
     */
    _setupNavigationHandlers(firstRender = false) {
        if (firstRender) {
            // Add event listeners only once
            const leftArrow = this.shadowRoot.querySelector('[data-slides-arrow-left]');
            const rightArrow = this.shadowRoot.querySelector('[data-slides-arrow-right]');

            if (leftArrow) {
                leftArrow.addEventListener('click', this._handlePrevClickBound);
            }
            if (rightArrow) {
                rightArrow.addEventListener('click', this._handleNextClickBound);
            }
        }
        // Note: _preActiveId and _posActiveId are always current because they're
        // instance properties accessed by the bound handlers
    }

    /**
     * Set up button click handlers.
     * @returns {void}
     * @private
     */
    _setupButtonHandlers(firstRender = false) {
        if (firstRender) {
            // Add event listeners only once
            const seeMoreButton = this.shadowRoot.querySelector('[data-see-more-button]');
            const seeReportButton = this.shadowRoot.querySelector(
                '[data-see-report-button]',
            );

            if (seeMoreButton) {
                seeMoreButton.addEventListener('click', this._handleSeeMoreClickBound);
            }
            if (seeReportButton) {
                seeReportButton.addEventListener(
                    'click',
                    this._handleSeeReportClickBound,
                );
            }
        }
        // Note: _active is always current because it's an instance property
        // accessed by the bound handlers
    }

    /**
     * Handle navigation to next/previous items.
     * @param {string} nextId - The ID of the next item to navigate to
     * @returns {void}
     * @private
     */
    _handleNavigation(nextId) {
        this._imageId = nextId;
        this._stopVideo();
        this._assignActiveIdToElements();
        this._handleActivesImages();
    }

    /**
     * Handle previous arrow click.
     * @returns {void}
     * @private
     */
    _handlePrevClick() {
        if (this._preActiveId) {
            this._handleNavigation(this._preActiveId);
        }
    }

    /**
     * Handle next arrow click.
     * @returns {void}
     * @private
     */
    _handleNextClick() {
        if (this._posActiveId) {
            this._handleNavigation(this._posActiveId);
        }
    }

    /**
     * Handle see more button click.
     * @returns {void}
     * @private
     */
    _handleSeeMoreClick() {
        if (!this._active) return;

        const img_uid = this._active.uid || this._active.id;

        updateSessionStoreValue(WESOG_SESSION_STORE_PARAMS.CURRENT_SELECTED_IMAGE, {
            uid: img_uid,
            ...this._active,
        });

        setTimeout(() => {
            goToDetailsView(img_uid);
        }, 500);
    }

    /**
     * Handle see report button click.
     * @returns {void}
     * @private
     */
    _handleSeeReportClick() {
        if (!this._active) return;
        goToReportDetails(this._active.id_event);
    }

    /**
     * Handle active images navigation and update component state.
     * @returns {void}
     * @private
     */
    _handleActivesImages() {
        const { active, preActiveId, posActiveId } = handleActivesImages(this._imageId);

        this._active = active;
        this._preActiveId = preActiveId;
        this._posActiveId = posActiveId;

        this.render();
    }

    /**
     * Assign active ID to elements and update their attributes.
     * @returns {void}
     * @private
     */
    _assignActiveIdToElements() {
        assignActiveIdToElements(this.shadowRoot, this._imageId, this._active);
    }

    /**
     * Reproduce video with proper error handling.
     * @returns {Promise<void>}
     * @private
     */
    async _reproduceVideo() {
        try {
            await reproduceVideo(
                this.shadowRoot,
                this._active,
                this._videoElement,
                this._fetchVideo,
            );
        } catch (error) {
            console.error('Error reproducing video:', error);
        }
    }

    /**
     * Stop video playback and clean up resources.
     * @returns {void}
     * @private
     */
    _stopVideo() {
        stopVideo(this._videoElement, this._fetchVideo);
    }

    /**
     * Translate the component elements.
     * @returns {void}
     * @private
     */
    _handleTranslationHelper() {
        updateUIFromShadowRoot(this.shadowRoot);
    }

    /**
     * Handle attribute changes for the component.
     * @param {MutationRecord[]} mutationsList - List of mutation records
     * @returns {void}
     * @private
     */
    _handleAttributeChange(mutationsList) {
        for (const mutation of mutationsList) {
            if (mutation.type !== 'attributes') continue;

            if (mutation.attributeName === ACTIVE_ID_ATTRIBUTE.toLowerCase()) {
                this._imageId = this.getAttribute(ACTIVE_ID_ATTRIBUTE);
                this._assignActiveIdToElements();
                this._handleActivesImages();
            }
        }
    }

    /**
     * Handle download options event.
     * @param {CustomEvent} event - The download options event
     * @returns {void}
     * @private
     */
    _handleDownloadOptions(event) {
        addDownloadOptionsList(this, event.detail.options, this._active.reference);
        this._handleTranslationHelper();
    }

    /**
     * Handle close modal event.
     * @returns {void}
     * @private
     */
    _handleCloseModal() {
        this._stopVideo();
    }

    /**
     * Add event listeners to the component.
     * @returns {void}
     * @private
     */
    _addEventListeners() {
        // Language update event
        document.addEventListener(
            UPDATE_LANGUAGE_EVENT,
            this._handleTranslationHelperBound,
        );

        // Attribute change observer
        this._observer = new MutationObserver(this._handleAttributeChangeBound);
        this._observer.observe(this, { attributes: true });

        // Download options event
        document.addEventListener(
            TRIGGER_SHOW_DOWNLOAD_OPTIONS_ZOOM_MODAL_EVENT,
            this._handleDownloadOptionsBound,
        );

        // Close modal event
        document.addEventListener(
            TRIGGER_CLOSE_COMMON_CUSTOM_MODAL_EVENT,
            this._handleCloseModalBound,
        );
    }

    /**
     * Remove event listeners from the component.
     * @returns {void}
     * @private
     */
    _removeEventListeners() {
        document.removeEventListener(
            UPDATE_LANGUAGE_EVENT,
            this._handleTranslationHelperBound,
        );
        document.removeEventListener(
            TRIGGER_SHOW_DOWNLOAD_OPTIONS_ZOOM_MODAL_EVENT,
            this._handleDownloadOptionsBound,
        );
        document.removeEventListener(
            TRIGGER_CLOSE_COMMON_CUSTOM_MODAL_EVENT,
            this._handleCloseModalBound,
        );
    }

    /**
     * Clean up resources and prevent memory leaks.
     * @returns {void}
     * @private
     */
    _cleanup() {
        if (this._observer) {
            this._observer.disconnect();
            this._observer = null;
        }

        if (this._timeoutId) {
            clearTimeout(this._timeoutId);
            this._timeoutId = null;
        }

        // Remove navigation event listeners
        const leftArrow = this.shadowRoot.querySelector('[data-slides-arrow-left]');
        const rightArrow = this.shadowRoot.querySelector('[data-slides-arrow-right]');
        const seeMoreButton = this.shadowRoot.querySelector('[data-see-more-button]');
        const seeReportButton = this.shadowRoot.querySelector('[data-see-report-button]');

        if (leftArrow) {
            leftArrow.removeEventListener('click', this._handlePrevClickBound);
        }
        if (rightArrow) {
            rightArrow.removeEventListener('click', this._handleNextClickBound);
        }
        if (seeMoreButton) {
            seeMoreButton.removeEventListener('click', this._handleSeeMoreClickBound);
        }
        if (seeReportButton) {
            seeReportButton.removeEventListener('click', this._handleSeeReportClickBound);
        }

        this._stopVideo();
    }
}

customElements.define(ZOOM_MODAL_WEB_COMPONENT, ZoomModalComponent);
